import {
  imDeleteFromArrayById,
  imPush,
  imSetToArray,
  imSetToObj,
} from '@client/shared/utils/immutableUtils/immutableUtils';
import {
  acceptAndRejectCampaignParticipants,
  deleteAdvertiser,
  deleteAdvertiserUser,
  fetchAdvertisers,
  fetchApprovedProfiles,
  fetchCampaignBrief,
  fetchCampaignParticipants,
  fetchCampaigns,
  fetchDraftDetails,
  fetchFollwersStats,
  fetchParticipants,
  fetchPublishedData,
  inviteAdvertiser,
  postAdvertiser,
  putAdvertiser,
  putAdvertiserImage,
  updateParticipantMediaFeedback,
} from '@client/connectivity/api.advertiser';
import { handleApiError } from '@client/redux/reduxUtils/reduxUtils';
import { showNotification } from '@client/redux/notification/notificationActions';
import { NOTIFICATION_TYPE } from '@client/containers/notificationListing/notification/Notification';

export const ADVERTISER_REDUCER_KEY = 'advertiserReducer';

export const LOAD_ADVERTISERS = 'LOAD_ADVERTISERS';
export const POST_AND_HANDLE_ADVERTISER = 'POST_AND_HANDLE_ADVERTISER';
export const PUT_AND_HANDLE_ADVERTISER = 'PUT_AND_HANDLE_ADVERTISER';
export const DELETE_AND_HANDLE_ADVERTISER = 'DELETE_AND_HANDLE_ADVERTISER';
export const PUT_AND_HANDLE_ADVERTISER_IMAGE =
  'PUT_AND_HANDLE_ADVERTISER_IMAGE';
export const SET_ADVERTISER_ERROR = 'SET_ADVERTISER_ERROR';
export const SET_CAMPAIGN_ERROR = 'SET_CAMPAIGN_ERROR';
export const LOAD_ADVERTISER_CAMPAIGNS = 'LOAD_ADVERTISER_CAMPAIGNS';
export const LOAD_CAMPAIGNS_PARTICIPANTS = 'LOAD_CAMPAIGNS_PARTICIPANTS';
export const LOAD_CAMPAIGNS_BRIEF = 'LOAD_CAMPAIGNS_BRIEF';
export const LOAD_FOLLWERS_STATS = 'LOAD_FOLLWERS_STATS';
export const LOAD_APPROVED_PROFILES = 'LOAD_APPROVED_PROFILES';
export const LOAD_PUBLISHED_DATA = 'LOAD_PUBLISHED_DATA';
export const LOAD_PARTICIPANTS = 'LOAD_PARTICIPANTS';
export const LOAD_DRAFT_INFO = 'LOAD_DRAFT_INFO';
export const SUBMIT_ADVERTISER_CAMPAIGN_DATA =
  'SUBMIT_ADVERTISER_CAMPAIGN_DATA';
export const SET_ADVERTISER_CAMPAIGN_DATA = 'SET_ADVERTISER_CAMPAIGN_DATA';
export const ERROR_ADVERTISER_CAMPAIGN_DATA = 'ERROR_ADVERTISER_CAMPAIGN_DATA';

/* DEFAULT FUNDRAISER STATE */
let defaultState = {
  advertisers: [],
  errors: {
    loadAdvertiser: null,
  },
  campaigns: null,
  campaignParticipants: null,
  campaignBrief: null,
  followersStatistics: null,
  approvedProfiles: null,
  publishedData: null,
  questions: null,
  participants: null,
  draftInfo: null,
  submitLoading: false,
  isSubmitSuccess: false,
  showQuestionsAndAnswers: null,
  showItemSelections: null,
  showAddressInformationAndPhoneNumber: null,
};

/* REDUCER */
export default function(state = defaultState, action = {}) {
  switch (action.type) {
    case LOAD_ADVERTISER_CAMPAIGNS: {
      return {
        ...state,
        campaigns: action.payload.campaigns,
      };
    }
    case LOAD_CAMPAIGNS_PARTICIPANTS: {
      return {
        ...state,
        campaignParticipants: action.payload,
      };
    }
    case LOAD_PARTICIPANTS: {
      return {
        ...state,
        participants: action.payload,
      };
    }
    case LOAD_DRAFT_INFO: {
      return {
        ...state,
        draftInfo: action.payload,
      };
    }
    case SUBMIT_ADVERTISER_CAMPAIGN_DATA: {
      return {
        ...state,
        submitLoading: true,
        isSubmitSuccess: false,
      };
    }
    case SET_ADVERTISER_CAMPAIGN_DATA: {
      return {
        ...state,
        submitLoading: false,
        isSubmitSuccess: true,
      };
    }
    case ERROR_ADVERTISER_CAMPAIGN_DATA: {
      return {
        ...state,
        submitLoading: false,
        isSubmitSuccess: false,
      };
    }
    case LOAD_FOLLWERS_STATS: {
      return {
        ...state,
        followersStatistics: action.payload,
      };
    }
    case LOAD_ADVERTISERS: {
      return { ...state, ...action.payload };
    }
    case LOAD_CAMPAIGNS_BRIEF: {
      return { ...state, campaignBrief: action.payload };
    }
    case LOAD_APPROVED_PROFILES: {
      return {
        ...state,
        approvedProfiles: action.payload.participants,
        questions: action.payload.questions,
        showQuestionsAndAnswers: action.payload.showQuestionsAndAnswers,
        showItemSelections: action.payload.showItemSelections,
        showAddressInformationAndPhoneNumber:
          action.payload.showAddressInformationAndPhoneNumber,
      };
    }
    case LOAD_PUBLISHED_DATA: {
      return {
        ...state,
        publishedData: action.payload,
      };
    }

    case POST_AND_HANDLE_ADVERTISER: {
      return {
        ...state,
        advertisers: imPush(state.advertisers, action.payload.advertiser),
      };
    }
    case PUT_AND_HANDLE_ADVERTISER: {
      return {
        ...state,
        advertisers: imSetToArray(
          state.advertisers,
          action.payload.advertiser,
          'id'
        ),
      };
    }
    case PUT_AND_HANDLE_ADVERTISER_IMAGE: {
      const { imageUrl, advertiserId } = action.payload;
      const { advertisers } = state;

      const advertiser = advertisers.find(
        (advertiser) => advertiser.id === advertiserId
      );
      const updatedAdvertiser = imSetToObj(advertiser, 'imageUrl', imageUrl);

      return {
        ...state,
        advertisers: imSetToArray(advertisers, updatedAdvertiser, 'id'),
      };
    }
    case DELETE_AND_HANDLE_ADVERTISER: {
      return {
        ...state,
        advertisers: imDeleteFromArrayById(
          state.advertisers,
          'id',
          action.payload
        ),
      };
    }
    case SET_ADVERTISER_ERROR: {
      return { ...state, errors: action.payload };
    }
    default:
      return state;
  }
}

/* ACTIONS */
export function loadAdvertisers(searchParams) {
  return function(dispatch) {
    return fetchAdvertisers(searchParams)
      .then((response) => {
        return dispatch({
          type: LOAD_ADVERTISERS,
          payload: response.data,
        });
      })
      .catch((error) => {
        return handleApiError(dispatch, error, SET_ADVERTISER_ERROR);
      });
  };
}

export function postAndHandleAdvertiser(advertiser) {
  return function(dispatch) {
    return postAdvertiser(advertiser)
      .then((response) => {
        return dispatch({
          type: POST_AND_HANDLE_ADVERTISER,
          payload: response.data,
        });
      })
      .catch((error) => {
        return handleApiError(dispatch, error, SET_ADVERTISER_ERROR);
      });
  };
}

export function putAndHandleAdvertiserImage(advertiserId, image) {
  return function(dispatch) {
    return putAdvertiserImage(advertiserId, image)
      .then((response) => {
        return dispatch({
          type: PUT_AND_HANDLE_ADVERTISER_IMAGE,
          payload: { advertiserId: advertiserId, ...response.data },
        });
      })
      .catch((error) => {
        return handleApiError(dispatch, error, SET_ADVERTISER_ERROR);
      });
  };
}

export function putAndHandleAdvertiser(advertiser) {
  return function(dispatch) {
    return putAdvertiser(advertiser, advertiser.id)
      .then((response) => {
        return dispatch({
          type: PUT_AND_HANDLE_ADVERTISER,
          payload: response.data,
        });
      })
      .catch((error) => {
        return handleApiError(dispatch, error, SET_ADVERTISER_ERROR);
      });
  };
}

export function deleteAndHandleAdvertiser(advertiserId) {
  return function(dispatch) {
    return deleteAdvertiser(advertiserId)
      .then(() => {
        return dispatch({
          type: DELETE_AND_HANDLE_ADVERTISER,
          payload: advertiserId,
        });
      })
      .catch((error) => {
        return handleApiError(dispatch, error, SET_ADVERTISER_ERROR);
      });
  };
}

export function inviteHandleAdvertiser(email, advertiserId) {
  return function(dispatch) {
    return inviteAdvertiser(email, advertiserId)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return handleApiError(dispatch, error, SET_ADVERTISER_ERROR);
      });
  };
}

export function deleteAdvertiserUserAndHandleAdvertiser(advertiserId, email) {
  return function(dispatch) {
    return deleteAdvertiserUser(advertiserId, email)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return handleApiError(dispatch, error, SET_ADVERTISER_ERROR);
      });
  };
}

export const selectAdvertisers = (state) => {
  return state[ADVERTISER_REDUCER_KEY].advertisers;
};

export function loadCampaigns() {
  return function(dispatch) {
    return fetchCampaigns()
      .then((response) => {
        return dispatch({
          type: LOAD_ADVERTISER_CAMPAIGNS,
          payload: response.data,
        });
      })
      .catch((error) => {
        return handleApiError(dispatch, error, SET_CAMPAIGN_ERROR);
      });
  };
}

export function loadCampaignBrief(campaignId) {
  return function(dispatch) {
    return fetchCampaignBrief(campaignId)
      .then((response) => {
        return dispatch({
          type: LOAD_CAMPAIGNS_BRIEF,
          payload: response.data,
        });
      })
      .catch((error) => {
        return handleApiError(dispatch, error, SET_ADVERTISER_ERROR);
      });
  };
}

export function loadCampaignPartcipants(campaignId) {
  return function(dispatch) {
    return fetchCampaignParticipants(campaignId)
      .then((response) => {
        return dispatch({
          type: LOAD_CAMPAIGNS_PARTICIPANTS,
          payload: response.data,
        });
      })
      .catch((error) => {
        return handleApiError(dispatch, error, SET_ADVERTISER_ERROR);
      });
  };
}

export function acceptAndRejectCampaignPartcipants(
  campaignId,
  participantsList
) {
  return function(dispatch) {
    return acceptAndRejectCampaignParticipants(campaignId, participantsList)
      .then((response) => {
        return dispatch({
          type: LOAD_CAMPAIGNS_PARTICIPANTS,
          payload: response.data,
        });
      })
      .catch((error) => {
        return handleApiError(dispatch, error, SET_ADVERTISER_ERROR);
      });
  };
}

export function loadFollowersStats(campaignId, influencersData) {
  return function(dispatch) {
    return fetchFollwersStats(campaignId, influencersData)
      .then((response) => {
        return dispatch({
          type: LOAD_FOLLWERS_STATS,
          payload: response.data,
        });
      })
      .catch((error) => {
        return handleApiError(dispatch, error, SET_ADVERTISER_ERROR);
      });
  };
}

export function loadApprovedProfiles(campaignId) {
  return function(dispatch) {
    return fetchApprovedProfiles(campaignId)
      .then((response) => {
        return dispatch({
          type: LOAD_APPROVED_PROFILES,
          payload: response.data,
        });
      })
      .catch((error) => {
        return handleApiError(dispatch, error, SET_ADVERTISER_ERROR);
      });
  };
}

export function loadParticipants(campaignId, influencerId) {
  return function(dispatch) {
    return fetchParticipants(campaignId, influencerId)
      .then((response) => {
        return dispatch({
          type: LOAD_PARTICIPANTS,
          payload: response.data,
        });
      })
      .catch((error) => {
        return handleApiError(dispatch, error, SET_ADVERTISER_ERROR);
      });
  };
}

export function loadPublishedData(campaignId) {
  return function(dispatch) {
    return fetchPublishedData(campaignId)
      .then((response) => {
        return dispatch({
          type: LOAD_PUBLISHED_DATA,
          payload: response.data,
        });
      })
      .catch((error) => {
        return handleApiError(dispatch, error, SET_ADVERTISER_ERROR);
      });
  };
}

export function loadDraftInfo(campaignId) {
  return function(dispatch) {
    return fetchDraftDetails(campaignId)
      .then((response) => {
        return dispatch({
          type: LOAD_DRAFT_INFO,
          payload: response.data,
        });
      })
      .catch((error) => {
        return handleApiError(dispatch, error, SET_ADVERTISER_ERROR);
      });
  };
}

export function updateParticipantFeedback(campaignId, media) {
  return function(dispatch) {
    dispatch({
      type: SUBMIT_ADVERTISER_CAMPAIGN_DATA,
    });

    return updateParticipantMediaFeedback(campaignId, media)
      .then(() => {
        dispatch(
          showNotification('Draft submitted', NOTIFICATION_TYPE.SUCCESS)
        );
        dispatch({
          type: SET_ADVERTISER_CAMPAIGN_DATA,
        });
        return dispatch(loadDraftInfo(campaignId));
      })
      .catch((error) => {
        dispatch({
          type: ERROR_ADVERTISER_CAMPAIGN_DATA,
        });
        return handleApiError(dispatch, error, SET_ADVERTISER_ERROR);
      });
  };
}

export const selectIsSubmitSuccess = (state) => {
  return state[ADVERTISER_REDUCER_KEY].isSubmitSuccess;
};

export const selectCampaignQuestions = (state) => {
  return state[ADVERTISER_REDUCER_KEY].questions;
};

export const selectApprovedProfiles = (state) => {
  return state[ADVERTISER_REDUCER_KEY].approvedProfiles;
};

export const selectFollowersStatistics = (state) => {
  return state[ADVERTISER_REDUCER_KEY].followersStatistics;
};

export const selectShowQuestionsAndAnswers = (state) => {
  return state[ADVERTISER_REDUCER_KEY].showQuestionsAndAnswers;
};

export const selectShowItemSelections = (state) => {
  return state[ADVERTISER_REDUCER_KEY].showItemSelections;
};

export const selectShowAddressInformationAndPhoneNumber = (state) => {
  return state[ADVERTISER_REDUCER_KEY].showAddressInformationAndPhoneNumber;
};
